import * as receiptActions from '@bca-receipt/store/receipt.actions';
import { ReceiptActionTypes } from '@bca-receipt/store/receipt.actions';
import { cloneProducts } from '@bca-shared/shared.constants';
import { IProduct } from '@bca-shared/models/product';
import { ICompany } from '@bca-shared/models/company';
import { IActionCode } from '@bca-shared/models/action-code';
import { IAgreement } from '@bca-shared/models/agreement';

export const INITIAL_STATE: IState = {
  products: [],
  totalPrice: 0,
  isVerified: false,
  isCompleted: false,
  company: null,
  actionCode: null,
  agreement: null
};

export interface IState {
  readonly products: IProduct[];
  readonly totalPrice: number;
  readonly isVerified: boolean;
  readonly isCompleted: boolean;
  readonly company: ICompany;
  readonly actionCode: IActionCode;
  readonly agreement: IAgreement;
}

export function reducer(state: IState = INITIAL_STATE, action: receiptActions.Action): IState {
  switch (action.type) {
    case ReceiptActionTypes.AddAgreement: {
      const agreement = action.payload;
      return {
        ...state,
        agreement
      };
    }
    case ReceiptActionTypes.DeleteAgreement: {
      return {
        ...state,
        agreement: null
      };
    }
    case ReceiptActionTypes.AddActionCode: {
      const actionCode = action.payload;
      return {
        ...state,
        actionCode
      };
    }
    case ReceiptActionTypes.DeleteActionCode: {
      return {
        ...state,
        actionCode: null
      };
    }
    case ReceiptActionTypes.AddCompany: {
      const company = action.payload;
      return {
        ...state,
        company
      };
    }
    case ReceiptActionTypes.DeleteCompany: {
      return {
        ...state,
        company: null
      };
    }
    case ReceiptActionTypes.AddUser: {
      const { user, productId } = action.payload;

      const products = cloneProducts(state.products);

      const product = {
        ...products.find(product => product.id === productId),
        user
      };

      products.splice(products.findIndex(product => product.id === productId), 1, product);

      return {
        ...state,
        products
      };
    }
    case ReceiptActionTypes.DeleteUser: {
      const { productId } = action.payload;

      const products = cloneProducts(state.products);
      const product = {
        ...products.find(product => product.id === productId)
      };
      products.splice(products.findIndex(product => product.id === productId), 1, {
        ...product,
        user: null
      });

      return {
        ...state,
        products
      };
    }
    case ReceiptActionTypes.AddProduct: {
      const products = cloneProducts(state.products);
      const product = action.payload;
      const totalProductPrice =
        product.price +
        product.addons
          .filter(addon => addon.selected)
          .reduce((total, addon) => total + addon.price, 0);

      products.unshift({
        ...product,
        totalPrice: totalProductPrice
      });

      const totalPrice = state.totalPrice + totalProductPrice;

      return {
        ...state,
        products,
        totalPrice
      };
    }
    case ReceiptActionTypes.DeleteProduct: {
      const { id } = action.payload;

      const products = state.products.filter(product => product.id !== id);

      const product = state.products.find(product => product.id === id);

      const totalPrice =
        state.totalPrice -
        product.price -
        product.addons
          .filter(addon => addon.selected)
          .reduce((total, addon) => total + addon.price, 0);

      return {
        ...state,
        products,
        totalPrice
      };
    }
    case ReceiptActionTypes.DeleteAddon: {
      const { addon, productId } = action.payload;

      const products = cloneProducts(state.products);
      const foundProduct = products.find(product => product.id === productId);

      const product = {
        ...foundProduct,
        addons: foundProduct.addons.map(a => {
          if (a.identifier === addon.identifier) {
            a.selected = false;
          }
          return a;
        })
      };

      products.splice(products.findIndex(product => product.id === productId), 1, product);

      const { price: addonPrice } = product.addons.find(a => a.identifier === addon.identifier);

      const totalPrice = state.totalPrice - addonPrice;

      return {
        ...state,
        products,
        totalPrice
      };
    }
    case ReceiptActionTypes.SetVerifiedStatus: {
      const { verified: isVerified } = action.payload;
      return {
        ...state,
        isVerified
      };
    }
    case ReceiptActionTypes.SetCompletedStatus: {
      const { completed: isCompleted } = action.payload;
      return {
        ...state,
        isCompleted
      };
    }
  }

  return state;
}
